<template>
  <v-dialog
    v-model="showDialog"
    max-width="600"
  >
    <v-card>
      <v-row class="fluid justify-space-between align-top ma-2">
        <v-card-title class="d-flex align-center">
          <v-icon size="26" color="warning" class="mr-3">mdi-alert-circle-outline</v-icon>
          <span class="text-h5"><span class="rounded-lg px-1">Generation Failures</span></span>
        </v-card-title>
        <v-btn variant="text" icon color="grey" @click="showDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-row>

      <v-card-text>
        <p class="mb-4">
          The AI service encountered a temporary issue, which can happen when the model is busy.
          You can try again, or select a different AI model before retrying the failed steps.
        </p>
        <v-list v-if="hasItemsWithType" density="compact" lines="one">
          <v-list-item
            v-for="(item, index) in failedItems"
            :key="index"
            class="pa-0"
          >
            <template #prepend>
              <v-icon
                size="small"
                :color="colors[item.type]"
              >
                {{ icons[item.type] || 'mdi-file-code-outline' }}
              </v-icon>
            </template>
            <v-list-item-title>
              <span class="text-capitalize font-weight-bold">{{ item.type.replace('-', ' ') }}: </span>
              <span>{{ getItemName(item) }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
        <div class="w-50 mt-6">
          <ai-version-selector v-model="selectedAiModel"/>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" size="large" @click="showDialog = false">Close</v-btn>
        <v-btn
          id="retry-generation-btn"
          variant="flat"
          color="primary"
          size="large"
          @click="handleRetry"
          :disabled="selectedAiModel !== workflow.aiVersion"
        >
          <v-icon start>mdi-sync</v-icon>
          Retry
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AiVersionSelector from '@/components/AiVersionSelector.vue'

const store = useStore()

const showDialog = computed({
  get: () => store.state.workflow.showRetryDialog,
  set: (value) => store.commit('workflow/setShowRetryDialog', value)
})

const failedItems = computed(() => store.state.workflow.failedGenerations)
const workflow = computed(() => store.state.workflow.workflow)
const selectedAiModel = ref(workflow.value?.aiVersion || 11)

const hasItemsWithType = computed(() => {
  return failedItems.value.some((item) => item.type)
})

const icons = {
  validation: 'mdi-check-circle-outline',
  command: 'mdi-flash',
  readmodel: 'mdi-magnify',
  entity: 'mdi-shape-outline',
  gwt: 'mdi-timeline',
  frontend: 'mdi-react',
  diagram: 'mdi-sitemap',
  cards: 'mdi-card-multiple-outline',
  commands: 'mdi-flash',
  queries: 'mdi-magnify',
  entities: 'mdi-shape-outline',
  event: 'mdi-calendar-star',
  card: 'mdi-card-text-outline',
  exampleData: 'mdi-code-json',
  comment: 'mdi-comment-text-outline'
}
const colors = {
  validation: 'green-darken-1',
  command: 'blue-lighten-1',
  readmodel: 'green-lighten-1',
  entity: 'amber-darken-1',
  gwt: 'purple-lighten-1',
  frontend: 'blue-lighten-2',
  diagram: 'red-lighten-1',
  cards: 'teal-lighten-1',
  commands: 'blue-lighten-1',
  queries: 'green-lighten-1',
  entities: 'amber-darken-1',
  event: 'orange-lighten-1',
  card: 'pink-lighten-1',
  exampleData: 'deep-purple-lighten-1',
  comment: 'blue-grey-lighten-1'
}

const getItemName = (item) => {
  if (item.name) {
    return item.name
  }

  return item.type
}

const handleRetry = async () => {
  await store.dispatch('workflow/retryFailedGenerations')
}

watch(() => workflow.value?.aiVersion, (newVal) => {
  selectedAiModel.value = newVal || 11
})
</script>
